import { useEffect, useRef } from 'react';
import {
  isAllowedOrigin,
  parseInbound,
  type InboundCommentPayload,
} from './iframeMessages';

interface Options {
  onComment: (payload: InboundCommentPayload) => void;
  onReady?: (pagePath: string, source: Window) => void;
  onRequestEdit?: (anchorId: string) => void;
  onUpdateComment?: (anchorId: string, body: string) => void;
}

export function useIframeComments(opts: Options): void {
  const onCommentRef = useRef(opts.onComment);
  const onReadyRef = useRef(opts.onReady);
  const onRequestEditRef = useRef(opts.onRequestEdit);
  const onUpdateCommentRef = useRef(opts.onUpdateComment);
  onCommentRef.current = opts.onComment;
  onReadyRef.current = opts.onReady;
  onRequestEditRef.current = opts.onRequestEdit;
  onUpdateCommentRef.current = opts.onUpdateComment;

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!isAllowedOrigin(event.origin)) return;
      const msg = parseInbound(event.data);
      if (!msg) return;
      if (msg.type === 'kb-dashboard:comment') {
        onCommentRef.current(msg.payload);
      } else if (msg.type === 'kb-dashboard:ready') {
        if (event.source) onReadyRef.current?.(msg.payload.pagePath, event.source as Window);
      } else if (msg.type === 'kb-dashboard:request-edit') {
        onRequestEditRef.current?.(msg.payload.anchorId);
      } else if (msg.type === 'kb-dashboard:update-comment') {
        onUpdateCommentRef.current?.(msg.payload.anchorId, msg.payload.body);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
}
